import { useState, useMemo, useEffect } from "react";
import { flattenStructure, loadStructure } from "components/Settings/WorkersTab/StructureEditor/treeUtils";
import styles from "./styles.module.scss";

interface IKadryItem {
  name: string;
  status: "active" | "archived";
}

const toItems = (names: string[]): IKadryItem[] =>
  names.map((name) => ({ name, status: "active" }));

const STOPNIE_DEFAULT: IKadryItem[] = toItems([
  "Główny specjalista",
  "Inspektor",
  "Młodszy referent",
  "Referent",
  "Specjalista",
  "Starszy inspektor",
  "Starszy referent",
]);

const STANOWISKA_DEFAULT: IKadryItem[] = toItems([
  "Archiwista",
  "Audytor wewnętrzny",
  "Główna księgowa",
  "Główny specjalista",
  "Kierownik referatu",
  "Naczelnik wydziału",
  "Przewodniczący Rady",
  "Radca prawny",
  "Sekretarka",
  "Sekretarz",
  "Skarbnik",
  "Specjalista",
  "Stażysta",
  "Wójt",
  "Zastępca wójta",
]);

const LS_STOPNIE = "kadry_stopnie_v1";
const LS_STANOWISKA = "kadry_stanowiska_v1";

const sortItems = (arr: IKadryItem[]): IKadryItem[] => {
  const cmp = (a: IKadryItem, b: IKadryItem) => a.name.localeCompare(b.name, "pl");
  return [
    ...arr.filter((i) => i.status === "active").sort(cmp),
    ...arr.filter((i) => i.status === "archived").sort(cmp),
  ];
};

function loadList(key: string, defaults: IKadryItem[]): IKadryItem[] {
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        if (typeof parsed[0] === "string") {
          // Migracja ze starego formatu string[]
          return sortItems((parsed as string[]).map((name) => ({ name, status: "active" as const })));
        }
        return sortItems(parsed as IKadryItem[]);
      }
    }
  } catch { /* ignore */ }
  return defaults;
}

function saveList(key: string, list: IKadryItem[]) {
  localStorage.setItem(key, JSON.stringify(list));
}

type SectionKey = "stopnie" | "stanowiska";

const Section = ({
  title,
  items,
  readonlyItems = [],
  open,
  onToggle,
  accentColor,
  searchQuery,
  onAdd,
  onEdit,
  onDelete,
  onToggleStatus,
}: {
  title: string;
  items: IKadryItem[];
  readonlyItems?: string[];
  open: boolean;
  onToggle: () => void;
  accentColor: string;
  searchQuery: string;
  onAdd: (name: string) => void;
  onEdit: (oldName: string, newName: string) => void;
  onDelete: (name: string) => void;
  onToggleStatus: (name: string) => void;
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [addValue, setAddValue] = useState("");
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  const q = searchQuery.toLowerCase();

  const activeItems = items.filter((i) => i.status === "active" && i.name.toLowerCase().includes(q));
  const archivedItems = items.filter((i) => i.status === "archived" && i.name.toLowerCase().includes(q));
  const filteredReadonly = readonlyItems.filter((s) => s.toLowerCase().includes(q));

  const activeCount = items.filter((i) => i.status === "active").length + readonlyItems.length;
  const archivedCount = items.filter((i) => i.status === "archived").length;

  const noResults = activeItems.length === 0 && archivedItems.length === 0 && filteredReadonly.length === 0 && !!q;

  const handleAdd = () => {
    const v = addValue.trim();
    if (v) onAdd(v);
    setAddValue("");
    setIsAdding(false);
  };

  const handleEdit = (item: IKadryItem) => {
    const v = editValue.trim();
    if (v && v !== item.name) onEdit(item.name, v);
    setEditingItem(null);
    setEditValue("");
  };

  const startEdit = (item: IKadryItem) => {
    setEditingItem(item.name);
    setEditValue(item.name);
  };

  const renderItem = (item: IKadryItem) => {
    const isArchived = item.status === "archived";

    if (editingItem === item.name) {
      return (
        <li key={item.name} className={styles.itemEdit}>
          <input
            className={styles.itemInput}
            value={editValue}
            autoFocus
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleEdit(item);
              if (e.key === "Escape") setEditingItem(null);
            }}
          />
          <button className={styles.btnSave} onClick={() => handleEdit(item)}>✓</button>
          <button className={styles.btnCancel} onClick={() => setEditingItem(null)}>✕</button>
        </li>
      );
    }

    return (
      <li key={item.name} className={`${styles.item} ${isArchived ? styles.itemArchived : ""}`}>
        <span className={styles.itemDot} style={{ background: accentColor, opacity: isArchived ? 0.3 : 0.75 }} />
        <span className={`${styles.itemText} ${isArchived ? styles.itemTextArchived : ""}`}>{item.name}</span>
        {isArchived && <span className={styles.archivedBadge}>Archiwalne</span>}
        <div className={styles.itemActions}>
          {!isArchived && (
            <button className={styles.btnEdit} title="Edytuj" onClick={() => startEdit(item)}>✎</button>
          )}
          <button
            className={isArchived ? styles.btnRestore : styles.btnArchive}
            title={isArchived ? "Przywróć" : "Archiwizuj"}
            onClick={() => onToggleStatus(item.name)}
          >
            {isArchived ? "↩ Przywróć" : "⊘"}
          </button>
          <button className={styles.btnDelete} title="Usuń" onClick={() => onDelete(item.name)}>✕</button>
        </div>
      </li>
    );
  };

  return (
    <div className={styles.section}>
      <button className={styles.sectionHeader} onClick={onToggle}>
        <span className={styles.sectionChevron} style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)" }}>▶</span>
        <span className={styles.sectionTitle}>{title}</span>
        <div className={styles.sectionCounts}>
          <span className={styles.sectionCount} style={{ background: accentColor + "22", color: accentColor }}>
            {activeCount}
          </span>
          {archivedCount > 0 && (
            <span className={styles.sectionCountArchived}>{archivedCount} arch.</span>
          )}
        </div>
      </button>
      {open && (
        <>
          <div className={styles.sectionBody}>
            <ul className={styles.itemList}>
              {activeItems.map(renderItem)}
              {filteredReadonly.map((name) => (
                <li key={`ro_${name}`} className={styles.item}>
                  <span className={styles.itemDot} style={{ background: accentColor, opacity: 0.35 }} />
                  <span className={styles.itemText}>{name}</span>
                  <span className={styles.readonlyBadge}>ze struktury</span>
                </li>
              ))}
              {archivedItems.length > 0 && (
                <>
                  <li className={styles.archivedSeparator}>
                    <span>Archiwalne</span>
                  </li>
                  {archivedItems.map(renderItem)}
                </>
              )}
              {noResults && (
                <li className={styles.emptyMsg}>Brak wyników dla „{searchQuery}"</li>
              )}
            </ul>
          </div>
          {isAdding ? (
            <div className={styles.addFormRow}>
              <input
                className={styles.itemInput}
                value={addValue}
                autoFocus
                placeholder="Nowa pozycja..."
                onChange={(e) => setAddValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAdd();
                  if (e.key === "Escape") { setIsAdding(false); setAddValue(""); }
                }}
              />
              <button className={styles.btnSave} onClick={handleAdd}>✓</button>
              <button className={styles.btnCancel} onClick={() => { setIsAdding(false); setAddValue(""); }}>✕</button>
            </div>
          ) : (
            <div className={styles.addRow}>
              <button className={styles.btnAdd} onClick={() => setIsAdding(true)}>+ Dodaj</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const KadroweTab = () => {
  const [open, setOpen] = useState<Record<SectionKey, boolean>>({ stopnie: true, stanowiska: true });
  const [stopnie, setStopnie] = useState<IKadryItem[]>(() => loadList(LS_STOPNIE, STOPNIE_DEFAULT));
  const [stanowiska, setStanowiska] = useState<IKadryItem[]>(() => loadList(LS_STANOWISKA, STANOWISKA_DEFAULT));
  const [search, setSearch] = useState("");
  const [structureRev, setStructureRev] = useState(0);

  useEffect(() => {
    const handler = () => setStructureRev((r) => r + 1);
    window.addEventListener("structureChanged", handler);
    return () => window.removeEventListener("structureChanged", handler);
  }, []);

  const structureItems = useMemo(() => {
    const fromStructure = flattenStructure(loadStructure())
      .filter((n) => !n.hasChildren)
      .map((n) => n.name);
    const customLower = new Set(stanowiska.map((s) => s.name.trim().toLowerCase()));
    return [...fromStructure.filter((s) => !customLower.has(s.trim().toLowerCase()))].sort((a, b) =>
      a.localeCompare(b, "pl")
    );
  }, [stanowiska, structureRev]);

  const toggle = (key: SectionKey) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const updateStopnie = (next: IKadryItem[]) => { setStopnie(next); saveList(LS_STOPNIE, next); };
  const updateStanowiska = (next: IKadryItem[]) => { setStanowiska(next); saveList(LS_STANOWISKA, next); };

  const isDuplicate = (list: IKadryItem[], name: string) =>
    list.some((s) => s.name.trim().toLowerCase() === name.trim().toLowerCase());

  // Stopnie handlers
  const handleAddStopien = (name: string) => {
    if (isDuplicate(stopnie, name)) return;
    updateStopnie(sortItems([...stopnie, { name, status: "active" }]));
  };
  const handleEditStopien = (oldName: string, newName: string) => {
    updateStopnie(sortItems(stopnie.map((s) => (s.name === oldName ? { ...s, name: newName } : s))));
  };
  const handleDeleteStopien = (name: string) => {
    updateStopnie(stopnie.filter((s) => s.name !== name));
  };
  const handleToggleStopien = (name: string) => {
    updateStopnie(sortItems(stopnie.map((s) =>
      s.name === name ? { ...s, status: s.status === "active" ? "archived" : "active" } : s
    )));
  };

  // Stanowiska handlers
  const handleAddStanowisko = (name: string) => {
    if (isDuplicate(stanowiska, name)) return;
    if (structureItems.some((s) => s.trim().toLowerCase() === name.trim().toLowerCase())) return;
    updateStanowiska(sortItems([...stanowiska, { name, status: "active" }]));
  };
  const handleEditStanowisko = (oldName: string, newName: string) => {
    updateStanowiska(sortItems(stanowiska.map((s) => (s.name === oldName ? { ...s, name: newName } : s))));
  };
  const handleDeleteStanowisko = (name: string) => {
    updateStanowiska(stanowiska.filter((s) => s.name !== name));
  };
  const handleToggleStanowisko = (name: string) => {
    updateStanowiska(sortItems(stanowiska.map((s) =>
      s.name === name ? { ...s, status: s.status === "active" ? "archived" : "active" } : s
    )));
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.searchBox}>
        <input
          className={styles.searchInput}
          placeholder="Szukaj..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button className={styles.searchClear} onClick={() => setSearch("")}>✕</button>
        )}
      </div>
      <Section
        title="Stopnie służbowe"
        items={stopnie}
        open={open.stopnie}
        onToggle={() => toggle("stopnie")}
        accentColor="#5d49c7"
        searchQuery={search}
        onAdd={handleAddStopien}
        onEdit={handleEditStopien}
        onDelete={handleDeleteStopien}
        onToggleStatus={handleToggleStopien}
      />
      <Section
        title="Stanowiska służbowe"
        items={stanowiska}
        readonlyItems={structureItems}
        open={open.stanowiska}
        onToggle={() => toggle("stanowiska")}
        accentColor="#1565c0"
        searchQuery={search}
        onAdd={handleAddStanowisko}
        onEdit={handleEditStanowisko}
        onDelete={handleDeleteStanowisko}
        onToggleStatus={handleToggleStanowisko}
      />
    </div>
  );
};

export default KadroweTab;
